import type { ReactNode } from 'react';
import { Card, Heading, Text } from 'frosted-ui';
import { Rise } from './animation';

interface BuyerNoticeProps {
  /** The seller's name, shown small above the heading when known. */
  eyebrow?: string | null;
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  /** Buttons, stacked full width. */
  actions?: ReactNode;
}

/**
 * A quiet, seller-neutral full-page message for buyers (thank-you, dead
 * links): no Paywall Lab branding, no loud colors. Works at 375px.
 */
export default function BuyerNotice({ eyebrow, icon, title, body, actions }: BuyerNoticeProps) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
        backgroundColor: 'var(--color-background)',
      }}
    >
      <Card size="3" className="vara2-step-in" style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
        {icon && (
          <Rise delay={60}>
            <div
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'var(--gray-a3)',
                color: 'var(--gray-12)',
                marginBottom: 'var(--space-4)',
              }}
            >
              {icon}
            </div>
          </Rise>
        )}
        <Rise delay={140}>
          {eyebrow && (
            <Text
              as="p"
              size="2"
              color="gray"
              weight="medium"
              style={{ margin: '0 0 var(--space-1)', overflowWrap: 'anywhere' }}
            >
              {eyebrow}
            </Text>
          )}
          <Heading as="h1" size="6" style={{ marginBottom: 'var(--space-2)', overflowWrap: 'anywhere' }}>
            {title}
          </Heading>
        </Rise>
        {body && (
          <Rise delay={220}>
            <Text as="p" size="3" color="gray" style={{ margin: 0, overflowWrap: 'anywhere' }}>
              {body}
            </Text>
          </Rise>
        )}
        {actions && (
          <Rise delay={320}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                marginTop: 'var(--space-5)',
              }}
            >
              {actions}
            </div>
          </Rise>
        )}
      </Card>
    </div>
  );
}
